/* eslint-disable max-lines */
import dayjs from "dayjs";
import { FC, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { ITripType } from "../../../interfaces/endpoints/trips/Trips.types";
import Input from "../../ui/input/Input";
import { SelectOption } from "../../ui/input/Input.types";
import SelectInput from "../../ui/input/SelectInput";
import useTimeList from "../../ui/input/TimePicker";
import CreateTripWrapper from "./schedules/CreateTripWrapper";

interface NewTripProps {
  response: any;
  editMode: boolean;
}

const NewTrip: FC<NewTripProps> = ({ response, editMode }) => {
  const {
    register,
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext<ITripType>();

  const [searchParams] = useSearchParams();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const arrivalRef = useRef<HTMLInputElement>(null);
  const currencyRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchParams.has("lineId") && !editMode) {
      const lineIdFromParams = searchParams.get("lineId");
      if (lineIdFromParams && !getValues("trip.lineId")) {
        setValue("trip.lineId", Number(lineIdFromParams), { shouldValidate: true });
      }
    }
  }, [searchParams, setValue, getValues, editMode, response]);

  useEffect(() => {
    if (!getValues("trip.defaultCurrencyId")) {
      setValue("trip.defaultCurrencyId", 1, { shouldValidate: true });
    }
  }, [setValue, getValues]);

  const updateDuration = () => {
    const ft = dayjs(`2000-01-01 ${getValues("trip.departureTimeString")}`);
    const tt = dayjs(`2000-01-01 ${getValues("trip.arrivalTimeString")}`);
    const mins: number = Number(tt.diff(ft, "minutes", true));
    setValue("trip.duration", mins);
  };

  const times = useTimeList();

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown((current) => current === dropdown ? null : dropdown);
  };

  const handleOptionSelect = (formField: keyof ITripType | string, option: SelectOption) => {
    const value = formField.includes("TimeString") ? option.id : Number(option.id);
    setValue(formField as any, value, { shouldValidate: true });
    setOpenDropdown(null);
  };

  return (
    <CreateTripWrapper title="Orari">
      <div className="absolute right-5 top-0 flex items-center flex-col">
        <div className="flex items-center transitions hover:scale-[1.01] cursor-pointer transition-all
        duration-75 select-none">
          <input
            type="checkbox"
            id="reserve"
            className="cursor-pointer text-primary bg-primary"
            {...register("trip.isReservable")}
          />
          <label
            htmlFor="reserve"
            className="ml-3 text-sm cursor-pointer text-primary font-semibold"
          >
            Bëje të rezervueshme në GjirafaTravel
          </label>
        </div>
        {typeof errors.trip?.isReservable?.message === "string" && (
          <div className="text-red-500 text-xs mt-0.5">
            {errors.trip?.isReservable?.message}
          </div>
        )}
      </div>
      <div className="md:flex-row flex-col md:flex w-full">
        <div className="mb-6 w-full md:w-1/2 md:pr-4">
          {!editMode ? (
            <SelectInput
              label="Zgjedh linjën"
              placeholder="Zgjedh"
              options={response?.data?.result?.map((el: any) => {
                return { name: el.label, id: el.value };
              }) || []}
              selectedId={
                getValues("trip.lineId") ||
                (searchParams.has("lineId") ? Number(searchParams.get("lineId")) : undefined)
              }
              isOpen={openDropdown === "line"}
              onClick={() => toggleDropdown("line")}
              onOptionSelect={(option) => handleOptionSelect("trip.lineId", option)}
              error={errors.trip?.lineId?.message}
            />
          ) : (
            <Input
              {...register("routeName")}
              label="Linja e zgjedhur"
              className="pointer-events-none"
              inputClassName="h-11"
            />
          )}
        </div>
        <div className="w-full md:w-1/2 flex">
          <div className="mb-6 w-2/5 pr-4">
            <SelectInput
              label="Ora e nisjes"
              options={times}
              selectedId={getValues("trip.departureTimeString")}
              isOpen={openDropdown === "departure"}
              onClick={() => toggleDropdown("departure")}
              onOptionSelect={(option) => {
                handleOptionSelect("trip.departureTimeString", option);
                updateDuration();
              }}
              onEnter={() => arrivalRef.current?.focus()}
              error={errors.trip?.departureTimeString?.message}
            />
          </div>
          <div className="mb-6 w-2/5 pr-4">
            <SelectInput
              inputRef={arrivalRef}
              label="Ora e arritjes"
              options={times}
              selectedId={getValues("trip.arrivalTimeString")}
              isOpen={openDropdown === "arrival"}
              onClick={() => toggleDropdown("arrival")}
              onOptionSelect={(option) => {
                handleOptionSelect("trip.arrivalTimeString", option);
                updateDuration();
              }}
              // 4. On Enter in Arrival, focus Currency
              onEnter={() => currencyRef.current?.focus()}
              error={errors.trip?.arrivalTimeString?.message}
            />
          </div>
          <SelectInput
            inputRef={currencyRef}
            label="Valuta"
            placeholder="Zgjedh"
            options={[
              { name: "Euro", id: 1 },
              { name: "Lekë", id: 2 }
            ]}
            selectedId={getValues("trip.defaultCurrencyId") || 1}
            isOpen={openDropdown === "currency"}
            onClick={() => toggleDropdown("currency")}
            onOptionSelect={(option) => handleOptionSelect("trip.defaultCurrencyId", option)}
            error={errors.trip?.defaultCurrencyId?.message}
            className="w-full"
          />
        </div>
      </div>
    </CreateTripWrapper>
  );
};

export default NewTrip;
